// Web app, Home module.
//

// React.
import React from 'react'

// Material UI.
import { makeStyles } from '@material-ui/core/styles'

// Material UI components.
import Grid from '@material-ui/core/Grid'
import Link from '@material-ui/core/Link'
import Paper from '@material-ui/core/Paper'
import Hidden from '@material-ui/core/Hidden'
import Button from '@material-ui/core/Button'
import ListItem from '@material-ui/core/ListItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

// Recharts.
import { ComposedChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Scatter, Legend, ResponsiveContainer } from 'recharts'

// App.
import * as common from './common'

// Tables (CSV).
import bBPLevels from '../tables/BP_Levels_for_Boys_by_Age_and_Height_Percentile.csv'
import gBPLevels from '../tables/BP_Levels_for_Girls_by_Age_and_Height_Percentile.csv'

const readCSVTable = file => {
    const resp = { meta: { sbp: { }, dbp: { }, heights: { } } }
    const memo = { }

    resp.sbp = { age: [ ], height: [ ], bp: [ ], bpp: [ ] }
    resp.dbp = { age: [ ], height: [ ], bp: [ ], bpp: [ ] }

    file.split('\n').forEach((row, i) => {
        row = row.split(',').filter(x => x != '')

        if (i > 2) {
            if ([3, 9, 15, 21, 27, 33, 39, 45, 51, 57, 63, 69, 75, 81, 87, 93, 99].includes(i)) { // Catch the age.
                memo.age = parseInt(row[0])

                resp.meta.sbp[memo.age] = { }
                resp.meta.dbp[memo.age] = { }

            } else if ([4, 10, 16, 22, 28, 34, 40, 46, 52, 58, 64, 70, 76, 82, 88, 94, 100].includes(i)) { // Catch the heights.
                memo.heights = row.slice(1).map(x => parseFloat(x))

                memo.heights.forEach((e, j) => {
                    if (j < 7) { // SBP.
                        resp.meta.sbp[memo.age][e] = { }

                    } else { // DBP.
                        resp.meta.dbp[memo.age][e] = { }
                    }
                })

                // Are the same heights for systolic and diastolic blood pressure.
                // So, just half of array is needed.
                resp.meta.heights[memo.age] = memo.heights.slice(0, 7)

            } else { // Iterate through the blood pressure values.
                const bpp = row[0].replace('and', '+') // Blood pressure percentile.

                row.slice(1).forEach((e, j) => {
                    if (j < 7) { // SBP.
                        resp.sbp.age.push(memo.age)
                        resp.sbp.height.push(memo.heights[j])
                        resp.sbp.bp.push(parseFloat(e))
                        resp.sbp.bpp.push(bpp)

                        resp.meta.sbp[memo.age][memo.heights[j]][bpp] = parseFloat(e)

                    } else { // DBP.
                        resp.dbp.age.push(memo.age)
                        resp.dbp.height.push(memo.heights[j])
                        resp.dbp.bp.push(parseFloat(e))
                        resp.dbp.bpp.push(bpp)

                        resp.meta.dbp[memo.age][memo.heights[j]][bpp] = parseFloat(e)
                    }
                })
            }
        }
    })

    return resp
}

const arrangeSeries = data => {
    const memo = { sbp: { }, dbp: { } }
    const resp = { sbp: [ ], dbp: [ ] }

    memo.ages = Array.from(Array(17).keys()).map(x => x + 1)

    memo.sbp['50th'] = Array(17).fill(0)
    memo.sbp['90th'] = Array(17).fill(0)
    memo.sbp['95th'] = Array(17).fill(0)
    memo.sbp['95th + 12 mm Hg'] = Array(17).fill(0)

    memo.dbp['50th'] = Array(17).fill(0)
    memo.dbp['90th'] = Array(17).fill(0)
    memo.dbp['95th'] = Array(17).fill(0)
    memo.dbp['95th + 12 mm Hg'] = Array(17).fill(0)

    for (let i = 0; i < data.sbp.bpp.length; i ++) {
        memo.sbp[data.sbp.bpp[i]][data.sbp.age[i] - 1] += data.sbp.bp[i] / 7
    }

    for (let i = 0; i < data.dbp.bpp.length; i ++) {
        memo.dbp[data.dbp.bpp[i]][data.dbp.age[i] - 1] += data.dbp.bp[i] / 7
    }

    for (const age of memo.ages) {
        const ps = { }
        const pd = { }

        ps.age = age.toString()
        ps['50th'] = common.round2int(memo.sbp['50th'][age - 1])
        ps['90th'] = common.round2int(memo.sbp['90th'][age - 1])
        ps['95th'] = common.round2int(memo.sbp['95th'][age - 1])
        ps['95th + 12 mm Hg'] = common.round2int(memo.sbp['95th + 12 mm Hg'][age - 1])

        pd.age = age.toString()
        pd['50th'] = common.round2int(memo.dbp['50th'][age - 1])
        pd['90th'] = common.round2int(memo.dbp['90th'][age - 1])
        pd['95th'] = common.round2int(memo.dbp['95th'][age - 1])
        pd['95th + 12 mm Hg'] = common.round2int(memo.dbp['95th + 12 mm Hg'][age - 1])

        resp.sbp.push(ps)
        resp.dbp.push(pd)
    }

    return resp
}

const formatPercentile = value => {
    if (value > 100) {
        return `100 + ${value % 100} mmHg`
    }

    return value
}

// Styling.
const useStyles = makeStyles(theme => ({
    page: {
        maxWidth: 1280,

        padding: 32,
        margin: 'auto',
        boxSizing: 'border-box',
        minHeight: 'calc(100vh - 64px)',

        [theme.breakpoints.down('sm')]: {
            padding: 16
        }
    },

    paper: {
        padding: '24px 16px'
    },

    'flat-card': {
        padding: '24px 16px',
        border: `1px solid ${theme.palette.divider}`
    },

    'flat-card-warn': {
        padding: '24px 16px',
        border: `1px solid ${theme.palette.secondary.main}`
    }
}))

const initialState = {
    // UI fields.
    age: '',
    height: '',
    sex: '',
    sbp: '',
    dbp: '',

    // Calculated fields.
    stages: { s: '', d: '' },
    sp: 0,
    dp: 0,

    // Loaded fields.
    bData: null,
    gData: null,

    graphicsSeries: { b: { sbp: [ ], dbp: [ ] }, g: { sbp: [ ], dbp: [ ] } },
    currentSeries: { sbp: [ ], dbp: [ ] },

    // Control fields.
    shownWarning: false,
    ready: false
}

// Components. {
export const Home = props => {
    const [ state, setState ] = React.useState(initialState)
    const respCompReference = React.useRef(null)
    const classes = useStyles()

    // Component variables.
    let disableButton = false

    disableButton = disableButton || !state.age
    disableButton = disableButton || !state.height
    disableButton = disableButton || !state.sex
    disableButton = disableButton || !state.sbp
    disableButton = disableButton || !state.dbp

    // Component handlers.
    const handeStateChange = (field, value) => {
        if (field != 'sex') {
            value = value.replace(/\D/g, '')
        }

        setState({ ...state, [field]: value })
    }

    const handleOnClick = () => {
        let { sex, age, height, sbp, dbp, bData, gData } = state
        let currentSeries = { sbp: [ ], dbp: [ ] }
        let shownWarning = false

        age = parseInt(age)
        height = parseFloat(height)
        sbp = parseFloat(sbp)
        dbp = parseFloat(dbp)

        let { stages, sp, dp } = common.calculateSAHStage(sex, age, height, sbp, dbp, [bData, gData])

        sp = parseInt(sp)
        dp = parseInt(dp)

        // console.log('Calculated stages:', stages)
        // console.log('Calculated SBP percentile:', sp)
        // console.log('Calculated DBP percentile:', dp)

        if (sex == 'Masculino') {
            currentSeries.sbp = state.graphicsSeries.b.sbp.map(e => ({ ...e }))
            currentSeries.dbp = state.graphicsSeries.b.dbp.map(e => ({ ...e }))

            currentSeries.sbp[age - 1]['P. A. de entrada'] = sbp
            currentSeries.dbp[age - 1]['P. A. de entrada'] = dbp

        } else { // Implies "sex == 'Feminino'".
            currentSeries.sbp = state.graphicsSeries.g.sbp.map(e => ({ ...e }))
            currentSeries.dbp = state.graphicsSeries.g.dbp.map(e => ({ ...e }))

            currentSeries.sbp[age - 1]['P. A. de entrada'] = sbp
            currentSeries.dbp[age - 1]['P. A. de entrada'] = dbp
        }

        shownWarning |= stages.s == 'PA abaixo do "Normal"'
        shownWarning |= stages.s == 'Estágio 2 de Hipertensão'
        shownWarning |= stages.d == 'PA abaixo do "Normal"'
        shownWarning |= stages.d == 'Estágio 2 de Hipertensão'

        setState({ ...state, stages, sp, dp, currentSeries, shownWarning })

        respCompReference.current.scrollIntoView({ behavior: 'smooth' })
    }

    // Component will mount.
    React.useEffect(() => {
        const bData = readCSVTable(bBPLevels)
        const gData = readCSVTable(gBPLevels)
        const graphicsSeries = { }

        graphicsSeries.b = arrangeSeries(bData)
        graphicsSeries.g = arrangeSeries(gData)

        setState({ ...state, bData, gData, graphicsSeries, ready: true })
    }, [])

    // Debug log.
    // console.log(state)

    return ( <>
        <div className={ classes.page }>
            <div className={ classes['flat-card'] } style={{ marginBottom: 16 }}>
                <Typography style={{ marginBottom: 8 }}>Ferramenta para calcular e analisar os níveis de Hipertensão Arterial Sistêmica (HAS) para crianças e adolescentes de 1 a 17 anos.</Typography>

                <Typography variant='caption'>O objetivo dessa ferramenta não é apresentar um diagnóstico final, mas sim auxiliar o profissional na análise da saúde do indivíduo.</Typography>
            </div>

            <Grid container spacing={ 2 }>
                <Grid item xs={ 12 }>
                    <Paper square className={ classes.paper }>
                        <Typography variant='h5' style={{ marginLeft: -2, marginBottom: 32 }}>Classificador</Typography>

                        <Grid container spacing={ 2 }>
                            <Grid item sm={ 12 } md={ 4 } lg={ 4 } xs={ 12 }>
                                <TextField fullWidth select size='small' variant='outlined' label='Sexo' value={ state.sex } onChange={ e => handeStateChange('sex', e.target.value) }>
                                    <ListItem value='Masculino'>Masculino</ListItem>
                                    <ListItem value='Feminino'>Feminino</ListItem>
                                </TextField>
                            </Grid>

                            <Grid item sm={ 12 } md={ 4 } lg={ 4 } xs={ 12 }>
                                <TextField fullWidth select size='small' variant='outlined' label='Idade (anos)' value={ state.age } onChange={ e => handeStateChange('age', e.target.value) }>
                                    { Array(17).fill().map((_, i) => <ListItem key={ i } value={ (i + 1).toString() }>{ i + 1 }</ListItem> ) }
                                </TextField>
                            </Grid>

                            <Grid item sm={ 12 } md={ 4 } lg={ 4 } xs={ 12 }>
                                <TextField fullWidth size='small' variant='outlined' label='Altura (cm)' value={ state.height } onChange={ e => handeStateChange('height', e.target.value) } />
                            </Grid>

                            <Grid item sm={ 12 } md={ 4 } lg={ 4 } xs={ 12 }>
                                <TextField fullWidth size='small' variant='outlined' label='P. A. sistólica (mm Hg)' value={ state.sbp } onChange={ e => handeStateChange('sbp', e.target.value) } />
                            </Grid>

                            <Grid item sm={ 12 } md={ 4 } lg={ 4 } xs={ 12 }>
                                <TextField fullWidth size='small' variant='outlined' label='P. A. diastólica (mm Hg)' value={ state.dbp } onChange={ e => handeStateChange('dbp', e.target.value) } />
                            </Grid>

                            <Grid item sm={ 12 } md={ 6 } lg={ 6 } xs={ 12 } style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <Typography variant='caption' color='textSecondary' >Preencha todos os campos para calcular.</Typography>
                            </Grid>

                            <Grid item sm={ 12 } md={ 6 } lg={ 6 } xs={ 12 } style={{ textAlign: 'right' }}>
                                <Button variant='contained' size='large' color='primary' disabled={ disableButton } onClick={ handleOnClick }>Calcular</Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={ 12 } innerRef={ respCompReference }>
                    <Grid container spacing={ 2 }>
                        <Grid item xs={ 12 }>
                            <Paper square className={ classes.paper }>
                                <Typography variant='h5' style={{ marginLeft: -2, marginBottom: 32 }}>Resultados</Typography>

                                <Grid container spacing={ 2 }>
                                    <Grid item sm={ 12 } md={ 6 } lg={ 6 } xs={ 12 }>
                                        <Typography variant='subtitle2'>Classificação da pressão arterial sistólica</Typography>
                                        <Typography variant='caption' color='textSecondary'>{ state.stages.s || '-' }</Typography>
                                    </Grid>

                                    <Grid item sm={ 12 } md={ 6 } lg={ 6 } xs={ 12 }>
                                        <Typography variant='subtitle2'>Classificação da pressão arterial diastólica</Typography>
                                        <Typography variant='caption' color='textSecondary'>{ state.stages.d || '-' }</Typography>
                                    </Grid>

                                    <Grid item sm={ 12 } md={ 6 } lg={ 6 } xs={ 12 }>
                                        <Typography variant='subtitle2'>Percentil sistólico estimado</Typography>
                                        <Typography variant='caption' color='textSecondary'>{ state.sp != 0 ? formatPercentile(state.sp) : '-' }</Typography>
                                    </Grid>

                                    <Grid item sm={ 12 } md={ 6 } lg={ 6 } xs={ 12 }>
                                        <Typography variant='subtitle2'>Percentil diastólico estimado</Typography>
                                        <Typography variant='caption' color='textSecondary'>{ state.dp != 0 ? formatPercentile(state.dp) : '-' }</Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        { state.shownWarning ? <Grid item xs={ 12 }>
                            <div className={ classes['flat-card-warn'] }>
                                <Typography color='error' style={{ marginBottom: 8 }}>Atenção!</Typography>

                                <Typography variant='caption'>A pressão arterial inserida se desvia consideravelmente da média, para o sexo { state.sex }, idade de { state.age } ano(s) e { state.height }cm de altura. Procure o profissional adequado para averiguar apropriadamente a situação.</Typography>
                            </div>
                        </Grid> : <>{/* FIXME: Rendering booleand value when statement is equal to "false". */}</> }

                        <Grid item sm={ 12 } md={ 6 } xs={ 12 }>
                            <Paper square className={ classes.paper }>
                                <Typography variant='h5' style={{ marginLeft: -2, marginBottom: 32 }}>Curvas de percentil para pressão sistólica</Typography>

                                { state.currentSeries.sbp.length > 0 ? <PercentileCurvesChart data={ state.currentSeries.sbp } />
                                : <Typography align='center' variant='subtitle2' style={{ lineHeight: 10 }} >Nenhum cálculo foi feito ainda.</Typography> }
                            </Paper>
                        </Grid>

                        <Grid item sm={ 12 } md={ 6 } xs={ 12 }>
                            <Paper square className={ classes.paper }>
                                <Typography variant='h5' style={{ marginLeft: -2, marginBottom: 32 }}>Curvas de percentil para pressão diastólica</Typography>

                                { state.currentSeries.dbp.length > 0 ? <PercentileCurvesChart data={ state.currentSeries.dbp } />
                                : <Typography align='center' variant='subtitle2' style={{ lineHeight: 10 }} >Nenhum cálculo foi feito ainda.</Typography> }
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <div className={ classes['flat-card'] } style={{ marginTop: 16 }}>
                <Typography style={{ marginBottom: 8 }}>Observações</Typography>

                <Typography variant='caption' display='block' style={{ marginBottom: 8 }}>A ferramenta foi desenvolvida como um trabalho de conclusão do curso de Engenharia de Computação no Centro Federal de Educação Tecnológica de Minas Gerais (CEFET-MG).</Typography>
                <Typography variant='caption' display='block'>O projeto teve como autor o aluno Gustavo Zille Pereira Vieira ({<Link href='mailto:guzillaine@gmail.com'>guzillaine@gmail.com</Link>}), como orientador o professor André Rodrigues da Cruz do Departamento de Computação (DECOM) do CEFET-MG e como coorientadora a professora Nilcemar Rodrigues Carvalho Cruz do Departamento de Nutrição da Universidade Federal de Juiz de Fora (UFJF).</Typography>
            </div>

            <div className={ classes['flat-card'] } style={{ marginTop: 16 }}>
                <Typography style={{ marginBottom: 8 }}>Referência</Typography>

                <Typography variant='caption'>A ferramenta se baseia na diretriz de prática clínica para triagem e controle de pressão alta em crianças e adolescentes publicada em "Pediatrics (2017) 140 (3): <Link href='https://doi.org/10.1542/peds.2017-1904'>e20171904</Link>".</Typography>

                {/* <Typography variant='caption'>Flynn JT, Kaelber DC, Baker-Smith CM, Blowey D, Carroll AE, Daniels SR, de Ferranti SD, Dionne JM, Falkner B, Flinn SK, Gidding SS, Goodwin C, Leu MG, Powers ME, Rea C, Samuels J, Simasek M, Thaker VV, Urbina EM; SUBCOMMITTEE ON SCREENING AND MANAGEMENT OF HIGH BLOOD PRESSURE IN CHILDREN. Clinical Practice Guideline for Screening and Management of High Blood Pressure in Children and Adolescents. Pediatrics. 2017 Sep;140(3):e20171904. doi: 10.1542/peds.2017-1904. Epub 2017 Aug 21. Erratum in: Pediatrics. 2017 Nov 30;: Erratum in: Pediatrics. 2018 Sep;142(3): PMID: 28827377.</Typography> */}
            </div>
        </div>
    </> )
}

const PercentileCurvesChart = props => (
    <>
        <Hidden smDown>
            <ResponsiveContainer aspect={ 16 / 9 } debounce={ 1 }>
                <ComposedChart margin={{ top: 0, right: 24, bottom: 40, left: 8 }} data={ props.data }>
                    <Line dataKey='95th + 12 mm Hg' stroke='#FF0000' />
                    <Line dataKey='95th' stroke='#FFA500' />
                    <Line dataKey='90th' stroke='#FFDD00' />
                    <Line dataKey='50th' stroke='#00FF00' />

                    <Scatter dataKey='P. A. de entrada' />

                    <CartesianGrid stroke='#CCC' />
                    <Legend margin={{ top: 20 }} wrapperStyle={{ bottom: 0 }} formatter={ (value, entry) => {
                        const { color } = entry

                        return ( <span style={{ display: 'inline-block', marginRight: 8, color }}>{ value }</span> )
                    } } />
                    <XAxis dataKey='age' label={{ value: 'Idade (anos)', position: 'bottom' }} />
                    <YAxis domain={ ['dataMin - 2', 'dataMax + 2'] } label={{ value: 'P. A. média (mm Hg)', angle: -90, position: 'insideBottomLeft', offset: 5 }} />
                    <Tooltip />
                </ComposedChart>
            </ResponsiveContainer>
        </Hidden>

        <Hidden mdUp>
            <ResponsiveContainer aspect={ 1 } debounce={ 1 }>
                <ComposedChart margin={{ top: 0, right: 8, bottom: 40, left: 2 }} data={ props.data }>
                    <Line dataKey='95th + 12 mm Hg' stroke='#FF0000' />
                    <Line dataKey='95th' stroke='#FFA500' />
                    <Line dataKey='90th' stroke='#FFDD00' />
                    <Line dataKey='50th' stroke='#00FF00' />

                    <Scatter dataKey='P. A. de entrada' />

                    <CartesianGrid stroke='#CCC' />
                    <Legend iconSize={ 10 } margin={{ top: 20 }} wrapperStyle={{ bottom: 0 }} />
                    <XAxis dataKey='age' label={{ value: 'Idade (anos)', position: 'insideBottom', offset: -10 }} />
                    <YAxis width={ 50 } domain={ ['dataMin - 2', 'dataMax + 2'] } label={{ value: 'P. A. média (mm Hg)', angle: -90, position: 'insideBottomLeft', offset: 10 }} />
                    <Tooltip />
                </ComposedChart>
            </ResponsiveContainer>
        </Hidden>
    </>
)
// }
